import { useState, useEffect, useRef } from 'react';
import { useFile, useSelection } from '../../context/AppContext.jsx';
import { useWaypointEditor } from '../../hooks/useWaypointEditor.js';
import { useIKSolver } from '../../hooks/useIKSolver.js';
import { toDisplayUnits } from '../../services/WaypointService.js';

export default function WaypointEditor() {
  const { currentWaypoints } = useFile();
  const { selectedWaypointIndex, selectedIsViaPoint, setSelectedWaypointIndex } = useSelection();
  const { updateWaypoint, changeConfig } = useWaypointEditor();
  const { getAllIKSolutions, applyIKConfig } = useIKSolver();
  const [formData, setFormData] = useState({
    posX: 0, posY: 0, posZ: 0,
    rotRX: 0, rotRY: 0, rotRZ: 0,
  });
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(0);
  const lastWaypointPose = useRef(null);

  useEffect(() => {
    if (selectedWaypointIndex !== null && currentWaypoints[selectedWaypointIndex]) {
      const waypoint = currentWaypoints[selectedWaypointIndex];
      const data = selectedIsViaPoint && waypoint.viaPoint
        ? waypoint.viaPoint
        : waypoint;

      // Display feature-local coordinates using WaypointService
      setFormData(toDisplayUnits(data.position, data.rotation));

      // Create a pose signature to detect if the pose actually changed
      const poseSignature = `${data.position.x},${data.position.y},${data.position.z},${data.rotation.rx},${data.rotation.ry},${data.rotation.rz}`;
      const poseChanged = lastWaypointPose.current !== poseSignature;
      lastWaypointPose.current = poseSignature;

      // Get all available IK configs for this waypoint
      // Only recalculate if the pose changed, not just the joint angles
      if (!selectedIsViaPoint && getAllIKSolutions && poseChanged) {
        const solutions = getAllIKSolutions(selectedWaypointIndex);
        setAvailableConfigs(solutions);
        
        // Find which config matches current joint positions
        if (waypoint.jointPositions && solutions.length > 0) {
          const currentConfigIndex = findMatchingConfig(waypoint.jointPositions, solutions);
          setSelectedConfig(currentConfigIndex >= 0 ? currentConfigIndex : 0);
        } else {
          setSelectedConfig(0);
        }
      } else if (selectedIsViaPoint) {
        setAvailableConfigs([]);
        setSelectedConfig(0);
      }
    }
  }, [selectedWaypointIndex, selectedIsViaPoint, currentWaypoints, getAllIKSolutions]);

  // Helper to find which config matches current joint positions
  const findMatchingConfig = (currentJoints, solutions) => {
    if (!currentJoints || solutions.length === 0) return -1;
    
    const threshold = 0.01; // ~0.57 degrees
    for (let i = 0; i < solutions.length; i++) {
      let matches = true;
      for (let j = 0; j < 6; j++) {
        if (Math.abs(currentJoints[j] - solutions[i][j]) > threshold) {
          matches = false;
          break;
        }
      }
      if (matches) return i;
    }
    return -1;
  };

  const handleUpdate = async () => {
    await updateWaypoint(formData);
  };

  const handleConfigChange = async (newConfigIndex) => {
    setSelectedConfig(newConfigIndex);
    // Pass the cached solutions to avoid recalculation
    await changeConfig(newConfigIndex, applyIKConfig, availableConfigs);
  };

  const handleCancel = () => {
    setSelectedWaypointIndex(null);
  };

  if (selectedWaypointIndex === null) return null;

  const waypoint = currentWaypoints[selectedWaypointIndex];
  if (!waypoint) return null;

  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold mb-4 pb-2 border-b-2 border-border flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span>Edit Waypoint</span>
      </h2>
      <div className="mb-4">
        <h3 className="font-medium">Waypoint #{waypoint.number}</h3>
        <span className="inline-block px-2 py-1 bg-primary bg-opacity-20 text-primary rounded text-sm">
          {waypoint.processType}
        </span>
      </div>

      <div className="space-y-4">
        {!selectedIsViaPoint && availableConfigs.length > 1 && (
          <div className="flex flex-col gap-2 p-3 bg-bg-secondary rounded border border-border">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Robot Configuration
            </h4>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-secondary">Config:</label>
              <select
                value={selectedConfig}
                onChange={(e) => handleConfigChange(parseInt(e.target.value))}
                className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm"
              >
                {availableConfigs.map((_, index) => (
                  <option key={index} value={index}>
                    Config {index + 1}
                  </option>
                ))}
              </select>
              <span className="text-xs text-text-secondary">
                {availableConfigs.length} available
              </span>
            </div>
            <p className="text-xs text-text-secondary">
              Change configuration to reach the same position with different joint angles
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Position (millimeters)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">X:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posX}
              onChange={(e) => setFormData({ ...formData, posX: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">Y:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posY}
              onChange={(e) => setFormData({ ...formData, posY: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">Z:</label>
            <input
              type="number"
              step="0.1"
              value={formData.posZ}
              onChange={(e) => setFormData({ ...formData, posZ: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">mm</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">Rotation (degrees)</h4>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RX:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRX}
              onChange={(e) => setFormData({ ...formData, rotRX: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RY:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRY}
              onChange={(e) => setFormData({ ...formData, rotRY: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="min-w-[30px] font-semibold text-text-secondary text-sm">RZ:</label>
            <input
              type="number"
              step="0.1"
              value={formData.rotRZ}
              onChange={(e) => setFormData({ ...formData, rotRZ: e.target.value })}
              className="flex-1 bg-bg-tertiary border border-border rounded px-2 py-1 text-text-primary text-sm font-mono"
            />
            <span className="min-w-[35px] text-sm text-text-secondary font-semibold">°</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleUpdate}
            className="flex-1 bg-primary text-white px-3 py-1.5 text-sm rounded hover:bg-blue-600 transition-colors"
          >
            Update Waypoint
          </button>
          <button 
            onClick={handleCancel}
            className="flex-1 bg-bg-tertiary text-text-primary px-3 py-1.5 text-sm rounded hover:bg-bg-hover transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}

